// Modern logout functionality for Amplify integration

import { signOut } from 'aws-amplify/auth'
import { useCallback, useContext } from 'react'

import { useAppDispatch } from '../../store'
import {USER_SIGNED_OUT} from '../auth/auth-actions'
import * as toastStack from '../shared/toast-stack/actions.js'

import { AuthContext } from './auth-context'

// Import legacy logout utilities
const stopHeartbeat = (): void => {
  // Legacy heartbeat cleanup - if it exists
  if (typeof (window as any)?.stopHeartbeat === 'function') {
    (window as any).stopHeartbeat()
  }
}

const stopPubsubListening = (): void => {
  // Legacy pubsub cleanup - if it exists
  if (typeof (window as any)?.stopPubsubListening === 'function') {
    (window as any).stopPubsubListening()
  }
}

const setCurrentUser = (user: any): void => {
  // Legacy user cleanup - if it exists
  if (typeof (window as any)?.setCurrentUser === 'function') {
    (window as any).setCurrentUser(user)
  }
}

export function useModernLogout (): {
  logout: (discardDialog?: any, callback?: () => void) => Promise<void>
  isLoggingOut: boolean
} {
  const dispatch = useAppDispatch()
  const { signOut: amplifySignOut } = useContext(AuthContext)

  const logout = useCallback(async (discardDialog?: any, callback?: () => void) => {
    try {
      console.log('🚪 Starting logout process...')

      // Clean up legacy components first
      stopHeartbeat()
      stopPubsubListening()
      discardDialog?.reset()

      // Use Amplify signOut if available, otherwise fall back to legacy
      if (amplifySignOut) {
        console.log('✅ Using Amplify signOut')
        await amplifySignOut()
      } else {
        console.log('🔄 Using legacy signOut')
        await signOut()
      }

      // Clean up legacy state
      setCurrentUser(null)

      // Dispatch Redux logout action
      dispatch({
        type: USER_SIGNED_OUT,
        forceRedirect: true
      })

      // Reset toast stack
      dispatch(toastStack.reset())

      console.log('✅ Logout completed successfully')
      callback?.()
    } catch (error) {
      console.error('❌ Logout failed:', error)

      // Even if Amplify logout fails, clean up local state
      try {
        setCurrentUser(null)
        dispatch({
          type: USER_SIGNED_OUT,
          forceRedirect: true
        })
        dispatch(toastStack.reset())
        callback?.()
      } catch (fallbackError) {
        console.error('❌ Fallback logout also failed:', fallbackError)
      }
    }
  }, [ dispatch, amplifySignOut ])

  return {
    logout,
    isLoggingOut: false // TODO: Add loading state if needed
  }
}

// Hook for legacy components that still use the old logout pattern
export function useLegacyCompatibleLogout (): (discardDialog?: any, callback?: () => void) => void {
  const { logout } = useModernLogout()

  return useCallback((discardDialog?: any, callback?: () => void) => {
    // Fire and forget for legacy compatibility
    logout(discardDialog, callback).catch(error => {
      console.error('Legacy logout failed:', error)
    })
  }, [ logout ])
}
